import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  type ListApprovalsResponse,
  TransferReviewBatchDecision,
  type UnifiedTransfer,
  approvalsListApprovals,
  approvalsListApprovalsOptions,
  transferCheckReviewTransfer,
} from '@column/api';
import { Button, Icon, Tooltip } from '@column/column-ui-kit';
import {
  Checkbox,
  DataTable,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@column/ui';

import { isApprovalHold, submitApprovalHold } from '../_utils/transferReview';
import { PageActions } from '~/app/Layout';
import { Loading } from '~/components';
import { Empty as TableEmpty, Pagination as TablePagination, tableDefaults } from '~/components/Table';
import { useOptimisticTransferUpdate } from '~/lib/api';
import { approvalListsFilter } from '~/lib/api/approvals';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { EASE } from '~/lib/approvals/components/ReviewDialog';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { transferTableColumns } from '~/lib/transfers';
import { type ReviewTargets, useCreateReviewBatch } from '~/lib/transfers/hooks/useCreateReviewBatch';
import { useInReviewTransferIds, useReviewBatchJobsStore } from '~/lib/transfers/stores/ReviewBatchJobs';
import { getTransferURL, pluralizeTransfers } from '~/lib/transfers/utils';
import { TransferEntryType } from '~/repositories';
import { isStepUpRequiredError } from '~/repositories/StepUpRepository';
import { TableGroupingMode, TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { Inner } from '~/styles';
import { TransferReviewDecision } from '~/typings/enum';

import { ReviewBatchDialog, type ReviewBatchRequest } from './_components/ReviewBatchDialog';
import {
  type ReviewListQuery,
  ReviewSingleDialog,
  type ReviewTransferFormValues,
} from './_components/ReviewSingleDialog';
import { type SelectionState, useApprovalSelection } from './_hooks/useApprovalSelection';

const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => entry.transfer) ?? [];

const TRANSFER_PARAM = 'transfer';

const getTargetIds = (targets: ReviewTargets): string[] => [...targets.transfer_ids, ...targets.ach_transfer_batch_ids];
const PERMALINK_MAX_PAGES = 20;

const SelectCell: React.FC<{
  checked: SelectionState;
  label: string;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ checked, label, disabled, onCheckedChange }) => (
  <label
    className={cn('-m-2 flex items-center self-stretch p-2', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
    onClick={(event) => event.stopPropagation()}
  >
    <Checkbox
      checked={checked}
      disabled={disabled}
      aria-label={label}
      onCheckedChange={(value) => onCheckedChange(value === true)}
    />
  </label>
);

interface ReviewSeed {
  transfers: UnifiedTransfer[];
  startIndex: number;
  currentIndex: number;
  total: number;
}

export const PageTransferApprovalsOverview: React.FC = () => {
  const { addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const optimisticallyUpdateTransfer = useOptimisticTransferUpdate();
  const { pageSize, timeFormat, groupingMode, update: updateTableConfig } = useTableConfig('approvals');
  const isBatchReviewEnabled = useFeatureFlag(FeatureFlag.EnableTransferReviewBatches);
  const showBatches = isBatchReviewEnabled && groupingMode === TableGroupingMode.Batched;
  const {
    clear: clearSelection,
    isEmpty: isSelectionEmpty,
    rowState,
    setRowsSelected,
    targets: selectedTargets,
    transferCount: selectedTransferCount,
  } = useApprovalSelection();
  const [batchReviewRequest, setBatchReviewRequest] = useState<ReviewBatchRequest>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false);
  const permalinkTransferId = useRef(searchParams.get(TRANSFER_PARAM)).current;
  const hasOpenedPermalinkRef = useRef(false);
  const setPermalink = useCallback(
    (transferId: string | undefined) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (transferId) next.set(TRANSFER_PARAM, transferId);
          else next.delete(TRANSFER_PARAM);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );
  const [reviewSeed, setReviewSeed] = useState<ReviewSeed>({ transfers: [], startIndex: 0, currentIndex: 0, total: 0 });
  const hasReviewedTransferRef = useRef(false);
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState({
    defaultPageSize: pageSize,
  });

  const reviewListQuery = useMemo<ReviewListQuery>(() => (showBatches ? { show_batches: true } : {}), [showBatches]);

  const listQuery = useMemo(
    () => ({
      ...reviewListQuery,
      status: 'pending' as const,
      limit: pageSize,
      ...(cursor ? { starting_after: cursor } : {}),
    }),
    [cursor, pageSize, reviewListQuery]
  );

  const { data, isFetching, isLoading } = useQuery({
    ...approvalsListApprovalsOptions({ query: listQuery }),
    placeholderData: keepPreviousData,
  });

  const hiddenIds = useReviewBatchJobsStore((state) => state.hiddenIds);
  const { hide, unhide, markReviewed } = useReviewBatchJobsStore.getState();
  const listedTransfers = useMemo(() => extractTransfers(data), [data]);
  const transfers = useMemo(
    () => listedTransfers.filter((transfer) => !hiddenIds.has(transfer.id)),
    [hiddenIds, listedTransfers]
  );
  const hasNext = data?.has_more ?? false;
  const totalResultsCount = data?.total_results_count ?? 0;

  const invalidateApprovals = useCallback(() => queryClient.invalidateQueries(approvalListsFilter), [queryClient]);

  const reviewTransfer = useCallback(
    async (id: string, transferType: TransferEntryType, transferStatus: string, req: ReviewTransferFormValues) => {
      let nextStatus: string | undefined;
      if (isApprovalHold(transferStatus)) {
        try {
          const transfer = await submitApprovalHold({ id, request: req, transferType });
          nextStatus = transfer.status;
        } catch (err) {
          addDangerNotification({
            content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
          });
          throw err;
        }
      } else if (transferType === 'check_debit') {
        try {
          const { data: transfer } = await transferCheckReviewTransfer({
            path: { check_transfer_id: id },
            body: { comment: req.comment, decision: req.decision },
            throwOnError: true,
          });
          nextStatus = transfer.status;
        } catch (err) {
          addDangerNotification({
            content: (err as Error)?.message ?? 'An error occurred while reviewing the transfer.',
          });
          throw err;
        }
      } else {
        addDangerNotification({ content: 'This transfer is not pending approval.' });
        return false;
      }

      if (!nextStatus) return false;
      optimisticallyUpdateTransfer({ id, status: nextStatus });
      return true;
    },
    [addDangerNotification, optimisticallyUpdateTransfer]
  );

  const createReviewBatch = useCreateReviewBatch();
  const inReviewIds = useInReviewTransferIds();
  const unavailableReviewIds = useMemo(() => new Set([...inReviewIds, ...hiddenIds]), [hiddenIds, inReviewIds]);
  const firstReviewableIndex = Math.max(
    listedTransfers.findIndex((transfer) => !unavailableReviewIds.has(transfer.id)),
    0
  );

  const lookupTransfers = useCallback(
    (ids: string[]) => {
      const wanted = new Set(ids);
      const found: Record<string, UnifiedTransfer> = {};
      queryClient.getQueriesData<ListApprovalsResponse>(approvalListsFilter).forEach(([, page]) =>
        extractTransfers(page).forEach((transfer) => {
          if (wanted.has(transfer.id)) found[transfer.id] = transfer;
        })
      );
      return found;
    },
    [queryClient]
  );

  const handleOptimisticSubmit = useCallback(
    (targets: ReviewTargets) => {
      clearSelection();
      hide(getTargetIds(targets));
    },
    [clearSelection, hide]
  );

  const openReview = useCallback(
    (decision: TransferReviewBatchDecision) =>
      setBatchReviewRequest({
        decision,
        transferCount: selectedTransferCount,
        targets: selectedTargets,
        onOptimisticSubmit: handleOptimisticSubmit,
        onSubmitFailed: () => unhide(getTargetIds(selectedTargets)),
        lookupTransfers,
      }),
    [handleOptimisticSubmit, lookupTransfers, selectedTargets, selectedTransferCount, unhide]
  );

  const reviewACHBatch = useCallback(
    async (batchId: string, req: ReviewTransferFormValues) => {
      try {
        return await createReviewBatch({
          decision:
            req.decision === TransferReviewDecision.Approved
              ? TransferReviewBatchDecision.APPROVED
              : TransferReviewBatchDecision.REJECTED,
          targets: { transfer_ids: [], ach_transfer_batch_ids: [batchId] },
          comment: req.comment,
        });
      } catch (err) {
        if (!isStepUpRequiredError(err)) {
          addDangerNotification({ content: getErrorMessage(err) });
        }
        throw err;
      }
    },
    [addDangerNotification, createReviewBatch]
  );

  const handleTimeFormatChange = useCallback(
    (value: TableTimeFormat) => {
      updateTableConfig({ timeFormat: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save time format preference' });
      });
    },
    [updateTableConfig, addDangerNotification]
  );

  const handleShowBatchesChange = useCallback(
    (checked: boolean) => {
      clearSelection();
      resetPagination();
      updateTableConfig({ groupingMode: checked ? TableGroupingMode.Batched : TableGroupingMode.Individual }).catch(
        () => {
          addDangerNotification({ content: 'Failed to save table settings' });
        }
      );
    },
    [addDangerNotification, clearSelection, resetPagination, updateTableConfig]
  );

  const selectableTransfers = useMemo(
    () => transfers.filter((transfer) => !inReviewIds.has(transfer.id)),
    [inReviewIds, transfers]
  );

  const pageSelectionState = useMemo<SelectionState>(() => {
    const states = selectableTransfers.map(rowState);
    if (states.length > 0 && states.every((state) => state === true)) return true;
    return states.some((state) => state !== false) ? 'indeterminate' : false;
  }, [rowState, selectableTransfers]);

  const columns = useMemo<DataTableColumnDef<UnifiedTransfer>[]>(
    () => [
      ...(isBatchReviewEnabled
        ? ([
            {
              id: 'select',
              header: (
                <SelectCell
                  checked={pageSelectionState}
                  label="Select all transfers on this page"
                  disabled={isFetching || selectableTransfers.length === 0}
                  onCheckedChange={(checked) => setRowsSelected(selectableTransfers, checked)}
                />
              ),
              width: 'min-content',
              cell: ({ row }) =>
                inReviewIds.has(row.id) ? (
                  <Tooltip content="In review" placement="right" delay={200} className="flex">
                    <span className="inline-flex animate-spin text-muted-foreground [&_svg]:size-4 [&_svg]:[--icon-color:currentColor]">
                      <Icon.CirclePendingThree />
                    </span>
                  </Tooltip>
                ) : (
                  <SelectCell
                    checked={rowState(row)}
                    label={`Select ${row.id}`}
                    onCheckedChange={(checked) => setRowsSelected([row], checked)}
                  />
                ),
            },
          ] satisfies DataTableColumnDef<UnifiedTransfer>[])
        : []),
      ...transferTableColumns({ timeFormat, onTimeFormatChange: handleTimeFormatChange }).filter(
        (column) => column.accessor !== 'status'
      ),
      {
        header: '',
        accessor: 'id',
        width: 'max-content',
        className: 'justify-end',
        cell: ({ row }) => (
          <Button
            onClick={(event: React.SyntheticEvent) => {
              event.stopPropagation();
              navigate(getTransferURL(row.id, row.type as TransferEntryType));
            }}
            size="tiny"
            variant="secondary"
          >
            View
            <Icon.ChevronRightLarge style={{ '--icon-size': '14px' } as React.CSSProperties} />
          </Button>
        ),
      },
    ],
    [
      handleTimeFormatChange,
      inReviewIds,
      isBatchReviewEnabled,
      isFetching,
      navigate,
      pageSelectionState,
      rowState,
      selectableTransfers,
      setRowsSelected,
      timeFormat,
    ]
  );

  const openReviewModal = useCallback(
    (index: number) => {
      hasReviewedTransferRef.current = false;
      const startIndex = pageIndex * pageSize;
      setReviewSeed({
        transfers: listedTransfers,
        startIndex,
        currentIndex: startIndex + index,
        total: totalResultsCount,
      });
      setIsApprovalModalOpen(true);
    },
    [listedTransfers, pageIndex, pageSize, totalResultsCount]
  );

  const handleRowClick = useCallback(
    (row: UnifiedTransfer) => {
      if (inReviewIds.has(row.id)) return;
      const index = listedTransfers.findIndex((transfer) => transfer.id === row.id);
      if (index >= 0) openReviewModal(index);
    },
    [inReviewIds, openReviewModal, transfers]
  );

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasOpenedPermalinkRef.current || !permalinkTransferId || listedTransfers.length === 0) return;
    hasOpenedPermalinkRef.current = true;

    const index = listedTransfers.findIndex((transfer) => transfer.id === permalinkTransferId);
    if (index >= 0) {
      openReviewModal(index);
      return;
    }

    const openFromLaterPage = async () => {
      let startingAfter = listedTransfers[listedTransfers.length - 1].id;
      let offset = listedTransfers.length;
      for (let attempt = 0; attempt < PERMALINK_MAX_PAGES; attempt += 1) {
        const { data: page } = await approvalsListApprovals({
          query: { ...reviewListQuery, status: 'pending', starting_after: startingAfter, limit: pageSize },
          throwOnError: true,
        });
        if (!isMountedRef.current) return;
        const pageTransfers = extractTransfers(page);
        if (pageTransfers.length === 0) break;
        const found = pageTransfers.findIndex((transfer) => transfer.id === permalinkTransferId);
        if (found >= 0) {
          hasReviewedTransferRef.current = false;
          setReviewSeed({
            transfers: pageTransfers,
            startIndex: offset,
            currentIndex: offset + found,
            total: page.total_results_count,
          });
          setIsApprovalModalOpen(true);
          return;
        }
        if (!page.has_more) break;
        startingAfter = pageTransfers[pageTransfers.length - 1].id;
        offset += pageTransfers.length;
      }
      setPermalink(undefined);
    };

    openFromLaterPage().catch(() => {
      if (isMountedRef.current) setPermalink(undefined);
    });
  }, [listedTransfers, openReviewModal, pageSize, permalinkTransferId, reviewListQuery, setPermalink]);

  const handleModalClose = useCallback(() => {
    setIsApprovalModalOpen(false);
    setPermalink(undefined);
    if (!hasReviewedTransferRef.current) return;
    hasReviewedTransferRef.current = false;
    invalidateApprovals();
  }, [invalidateApprovals, setPermalink]);

  const handlePageSizeChange = useCallback(
    (value: number) => {
      setPageSize(value);
      updateTableConfig({ limit: value }).catch(() => {
        addDangerNotification({ content: 'Failed to save page size preference' });
      });
    },
    [setPageSize, updateTableConfig, addDangerNotification]
  );

  const settingsMenu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <span className="inline-flex">
          <Button size="small" variant="secondary" icon={<Icon.Settings />} ariaLabel="Table settings" />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="min-w-56 p-3">
        {isBatchReviewEnabled && (
          <>
            <DropdownMenuLabel className="px-0 pb-2 pt-0 text-xs text-muted-foreground">
              Grouping Mode
            </DropdownMenuLabel>
            <Select
              value={showBatches ? 'batched' : 'individual'}
              onValueChange={(value) => handleShowBatchesChange(value === 'batched')}
            >
              <SelectTrigger size="sm" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="individual">Individual</SelectItem>
                <SelectItem value="batched">Batched</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}
        <DropdownMenuLabel
          className={cn('px-0 pb-2 text-xs text-muted-foreground', isBatchReviewEnabled ? 'pt-3' : 'pt-0')}
        >
          Time Format
        </DropdownMenuLabel>
        <Select value={timeFormat} onValueChange={(value) => handleTimeFormatChange(value as TableTimeFormat)}>
          <SelectTrigger size="sm" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TableTimeFormat.Relative}>Relative Time</SelectItem>
            <SelectItem value={TableTimeFormat.Absolute}>Date / Time</SelectItem>
          </SelectContent>
        </Select>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      <ReviewBatchDialog request={batchReviewRequest} onClose={() => setBatchReviewRequest(undefined)} />
      <ReviewSingleDialog
        listQuery={reviewListQuery}
        transfers={reviewSeed.transfers}
        transferStartIndex={reviewSeed.startIndex}
        currentIndex={reviewSeed.currentIndex}
        totalTransfers={reviewSeed.total}
        isOpen={isApprovalModalOpen}
        onClose={handleModalClose}
        onCurrentTransferChange={setPermalink}
        unavailableIds={unavailableReviewIds}
        onSubmit={async (transferId, transferType, transferStatus, request) => {
          if (transferType === 'ach_batch') {
            hide([transferId]);
            try {
              const reviewBatch = await reviewACHBatch(transferId, request);
              hasReviewedTransferRef.current = true;
              return reviewBatch;
            } catch (error) {
              unhide([transferId]);
              throw error;
            }
          }
          const isReviewed = await reviewTransfer(transferId, transferType, transferStatus, request);
          if (!isReviewed) return undefined;
          markReviewed([transferId]);
          hasReviewedTransferRef.current = true;
          return undefined;
        }}
      />
      <PageActions>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={isSelectionEmpty || !isBatchReviewEnabled ? 'review' : 'selection'}
            initial={{ opacity: 0, x: 8, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -8, scale: 0.95, transition: { duration: 0.1, ease: EASE } }}
            transition={{ duration: 0.15, ease: EASE }}
            className="flex items-center gap-2"
          >
            {isSelectionEmpty || !isBatchReviewEnabled ? (
              <>
                {settingsMenu}
                {transfers.length > 0 && (
                  <Button onClick={() => openReviewModal(firstReviewableIndex)} size="small">
                    Review Transfers
                  </Button>
                )}
              </>
            ) : (
              <>
                <div className="mr-1.5 flex items-center gap-2.5">
                  <span className="whitespace-nowrap text-sm font-medium text-muted-foreground">
                    {pluralizeTransfers(selectedTransferCount)} selected
                  </span>
                  <Button size="small" variant="subtle-primary" className="!p-0" onClick={clearSelection}>
                    Reset
                  </Button>
                </div>
                <Button
                  size="small"
                  variant="danger"
                  icon={<Icon.Cross />}
                  onClick={() => openReview(TransferReviewBatchDecision.REJECTED)}
                >
                  Reject
                </Button>
                <Button
                  size="small"
                  icon={<Icon.Checkmark />}
                  onClick={() => openReview(TransferReviewBatchDecision.APPROVED)}
                >
                  Approve
                </Button>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </PageActions>

      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<UnifiedTransfer>
            {...tableDefaults}
            columns={columns}
            data={transfers}
            isLoading={isFetching}
            emptyContent={
              <TableEmpty
                icon={<Icon.AnimationArrows />}
                title="No transfers found"
                description="No transfers are awaiting approval right now."
              />
            }
            rowKey={(row) => row.id}
            rowClassName={(row) => cn(tableDefaults.rowClassName, inReviewIds.has(row.id) && 'opacity-60')}
            onRowClick={handleRowClick}
          />

          <TablePagination
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            current={pageIndex + 1}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            onPrevious={onPrevious}
            onNext={() => {
              if (transfers.length > 0) onNext({ cursor: transfers[transfers.length - 1].id });
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
